/* eslint-disable no-inner-declarations */
/* eslint-disable @typescript-eslint/no-namespace */
import * as Sentry from '@sentry/remix';
import type { Entrypoint, PAGE_POSITIONS, PageType } from 'types';
import type { ProductListItem } from 'types/capi/shared';
import {
  fromProductListItem,
  type TrackableProduct
} from 'types/TrackableProduct';
import type { User } from 'types/User';

const GTM_UNDEFINED_ERR_MSG =
  'window.dataLayer is not defined. This could mean your google analytics script has not loaded on the page yet.';

declare global {
  interface Window {
    dataLayer?: [unknown];
    gtag: (
      option: string,
      gaTrackingId: string,
      options: Record<string, unknown>
    ) => void;
  }
}

type DataLayerParams = {
  articleType?: string;
  pageType: PageType;
  session: string | null;
  user: User;
};

function pushToDataLayer(data: object) {
  if (!window.dataLayer) return Sentry.captureMessage(GTM_UNDEFINED_ERR_MSG);

  window.dataLayer.push(data);
}

namespace Ecommerce {
  type GtmItem = {
    index?: number;
    item_brand?: string;
    item_category?: string; // gender
    item_category2?: string; // footwear | clothing
    item_id?: string; // style code
    item_name: string;
    item_variant?: string; // sub brand
    location_id?: string; //stock status
    price: number | null;
  };

  function flushEcommerceFromDataLayer() {
    pushToDataLayer({ ecommerce: null });
  }

  function pushEcommerceToDataLayer(ecommerceDataLayer: {
    [key: string]: unknown;
    ecommerce: object;
    event: string;
  }) {
    flushEcommerceFromDataLayer();

    return pushToDataLayer(ecommerceDataLayer);
  }

  export function getEcommerceItem(
    product: TrackableProduct,
    position?: number
  ): GtmItem {
    return {
      index: position,
      item_brand: product.brand,
      item_category: product.genders?.join(', '),
      item_category2: product.sub_type,
      item_id: product.style_code,
      item_name: product.name,
      item_variant: product.model,
      location_id: product.stock_status,
      price: product.price.sale ?? product.price.regular
    };
  }

  type ViewItemExtraData = {
    entry_point?: Entrypoint;
    is_logged_in?: boolean;
    list_id?: string;
    num_of_listings?: number;
    page_path?: string;
    page_type?: string;
    platform?: string;
    previous_page_path?: string;
    previous_page_type?: PageType;
    product_brand?: string;
    product_id?: number;
    product_view_price?: number;
    source_medium?: string;
    style_code?: string;
    user_id?: string;
  };

  export function viewListItem(
    product: TrackableProduct,
    position?: number,
    extraData?: ViewItemExtraData
  ) {
    return pushEcommerceToDataLayer({
      ...extraData,
      ecommerce: {
        currency: product.price.currency,
        items: [getEcommerceItem(product, position)],
        sole_market_option: getSoleMarketOption(product),
        value: product.price.sale ?? product.price.regular
      },
      event: 'view_item',
      stock_status: product.stock_status
    });
  }

  export function viewListItems(
    products: ProductListItem[],
    path: string,
    extra?: object
  ) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        currency: products[0]?.price.currency,
        item_list_name: path,
        items: products.map((p, i) =>
          getEcommerceItem(fromProductListItem(p), i + 1)
        )
      },
      event: 'view_item_list',
      ...extra
    });
  }

  export function addToCart({
    buttonPosition,
    currency,
    affiliateName,
    isReseller,
    item,
    price,
    pagePosition,
    previousPagePosition
  }: {
    affiliateName: string;
    buttonPosition: number;
    currency?: string;
    isReseller: boolean;
    item: GtmItem;
    pagePosition: PAGE_POSITIONS;
    previousPagePosition?: string;
    price?: number;
  }) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        button_position: buttonPosition,
        currency,
        is_reseller: isReseller,
        items: [item],
        page_position: pagePosition,
        previous_page_position: previousPagePosition,
        reseller: affiliateName,
        value: price
      },
      event: 'add_to_cart'
    });
  }

  export function addToWishlist(product: TrackableProduct) {
    return pushEcommerceToDataLayer({
      ecommerce: {
        currency: product.price.currency,
        items: [getEcommerceItem(product)],
        sole_market_option: getSoleMarketOption(product),
        value: product.price.sale ?? product.price.regular
      },
      event: 'add_to_wishlist'
    });
  }
}

function getSoleMarketOption(product: TrackableProduct) {
  if (product.stock_listings)
    return product.stock_listings.affiliates.some(
      (a) => a.affiliate.id === 'affiliate:marketplace'
    );
}

function addDefaultDataToLayer({
  pageType,
  user,
  session,
  articleType
}: DataLayerParams) {
  return pushToDataLayer({
    account_id: user.id,
    article_type: articleType || '',
    is_buyer: user.profile?.is_buyer ?? false,
    is_logged_in: Boolean(user.profile),
    is_seller: user.profile?.is_seller ?? false,
    is_seller_verified: user.profile?.seller_registration_status === 'VERIFIED',
    page_type: pageType,
    session: session
  });
}

function sendUserInteractionEvent(action: string, pagePosition: string) {
  return pushToDataLayer({
    action,
    event: 'userInteraction',
    page_position: pagePosition
  });
}

type ProductFilterGtmEvent = {
  filterName: string;
  filterValue: string;
  pagePosition: string;
};

function sendPlpProductFilterEvent({
  filterName,
  filterValue,
  pagePosition
}: ProductFilterGtmEvent) {
  return pushToDataLayer({
    event: 'productFilter',
    filter_name: filterName,
    filter_value: filterValue,
    page_position: pagePosition
  });
}

export enum LoginSource {
  Apple = 'APPLE',
  Email = 'EMAIL',
  Google = 'GOOGLE',
  Meta = 'FACEBOOK'
}

function sendLoginEvent(uid: string | undefined = '', source: LoginSource) {
  return pushToDataLayer({
    account_id: uid,
    account_type: source,
    event: 'user_login'
  });
}

function loadPixels() {
  return pushToDataLayer({ event: 'load_pixels' });
}

export type TrackedButtonClickName =
  | 'ALL_SIZES'
  | 'BLACKFRIDAY_BANNER'
  | 'BRAND_CARD'
  | 'CANCEL_NOTIFICATIONS'
  | 'COMPARE_ALL_RETAILERS'
  | 'CONFIRM_EMAIL_STATUS'
  | 'DISCOVER_DEALS'
  | 'DISCOVER_MORE_FEATURES'
  | 'DOWNLOAD_THE_APP'
  | 'EDITORIAL_POLICY'
  | 'EXISTING_USER_LOGIN_PROMPT'
  | 'GATED_RETAILER_CLICK'
  | 'GO_TO_REGION'
  | 'I_WANT_NOTIFICATIONS'
  | 'ILL_CHECK_MY_INBOX'
  | 'LATEST_FEATURES'
  | 'LATEST_NEWS'
  | 'MID_PROMO_BANNER'
  | 'MOBILE_FILTERS_CLEAR_ALL'
  | 'MOBILE_FILTERS_SHOW_RESULTS'
  | 'NOTIFICATION_CENTRE'
  | 'NOTIFY_ME_SIZE_MARKET_CTA'
  | 'OPEN_NOTIFICATION'
  | 'PRODUCT_CARD'
  | 'PRODUCT_CLICK'
  | 'PROFILE_ICON'
  | 'REGISTER_AS_A_SELLER'
  | 'RELATED_ARTICLES'
  | 'SALE_PRODUCTS_HIGHLIGHT_CTA'
  | 'SAVE_PRODUCT'
  | 'SELL_NOW'
  | 'SHARE_TO_FACEBOOK'
  | 'SHARE_TO_TWITTER'
  | 'SHOW_ALL_RAFFLES'
  | 'SHOW_ALL_RETAILERS'
  | 'SIGN_IN_APPLE'
  | 'SIGN_IN_EMAIL'
  | 'SIGN_IN_FACEBOOK'
  | 'SIGN_IN_GOOGLE'
  | 'SIGN_UP_EMAIL_PROMPT'
  | 'SIGN_UP_EMAIL_SUBSCRIPTION_BANNER'
  | 'SIGN_UP_EMAIL'
  | 'SILHOUETTE_CARD'
  | 'SOCIAL_MEDIA_CAROUSEL'
  | 'SOCIAL_MEDIA_PROFILE'
  | 'SOLE_MARKET_BUY_NOW'
  | 'SPLASH_CTA'
  | 'STAY_ON_UK'
  | 'TOP_CATEGORY'
  | 'TOP_PROMO_BANNER'
  | 'UK_SITE'
  | 'USP_CARD'
  | 'VIEW_ALL'
  | 'VIEW_MORE'
  | 'WHATS_HOT'
  | 'WTB_VIEW_TOGGLE_RAFFLES'
  | 'WTB_VIEW_TOGGLE_RETAILERS'
  | 'X'
  | `FEATURES_${string}`
  | `GRID_SIZE_TOGGLE_${string}`
  | `HOMEPAGE_CAROUSEL_${string}`
  | `PAGINATION_${string}`
  | `PLP_CAROUSEL_${string}`
  | `PRODUCT_CARD_${string}`
  | `SIZE_${string}`
  | `Sole Market Hub - ${string}`
  | `SOLE_MARKET_FAQ_${string}`
  | `SPLASH_POSITION_${string}`;

type ButtonClickEventData = {
  affiliate?: string;
  buttonName: TrackedButtonClickName;
  pagePosition: string;
};

function trackedButtonClick({
  affiliate,
  buttonName,
  pagePosition
}: ButtonClickEventData) {
  return pushToDataLayer({
    ...(affiliate && { affiliate }),
    button_name: buttonName,
    event: 'TrackedButtonClick',
    page_position: pagePosition
  });
}

type ProductCardClickData = {
  entry_point: Entrypoint;
  is_click_out: boolean;
  item_list_name: string;
  list_id: string | null;
  page_position: PAGE_POSITIONS;
  page_type?: PageType;
  platform: 'web';
  position?: number;
  previous_page_path?: string;
  previous_page_position?: string;
  product_brand: string;
  product_id: number;
  product_name: string;
  product_view_price?: number;
  source_medium?: string;
  stock_status: string;
  style_code: string;
};

function productCardClick(data: ProductCardClickData) {
  return pushToDataLayer({
    event: 'product_card_click',
    ...data
  });
}

function recordFeatureFlag(name: string, isEnabled: boolean) {
  return pushToDataLayer({
    event: 'feature_flag',
    feature_flag_is_enabled: isEnabled,
    feature_flag_name: name
  });
}

type NotifyMeSetData = {
  pagePosition: string;
  previousPagePosition?: string;
  productId?: number;
  productName?: string;
};

function notifyMeSet({
  pagePosition,
  previousPagePosition,
  productId,
  productName
}: NotifyMeSetData) {
  return pushToDataLayer({
    event: 'notify_me_set',
    page_position: pagePosition,
    previous_page_position: previousPagePosition,
    ...(productId != null && { product_id: productId }),
    ...(productName != null && { product_name: productName })
  });
}

export default {
  Ecommerce,
  addDefaultDataToLayer,
  loadPixels,
  notifyMeSet,
  productCardClick,
  pushToDataLayer,
  recordFeatureFlag,
  sendLoginEvent,
  sendPlpProductFilterEvent,
  sendUserInteractionEvent,
  trackedButtonClick
};
